import { defineStore } from 'pinia';
import { Rebroadcasts } from '@/api/rebroadcasts.js';
import axios from 'axios'

export const rebroadcastsStore = defineStore('storeRebroadcasts', {
  state: () => ({
    allRebroadcastsMax: 0,
    allRebroadcasts: [],
    allRebroadcastsFiltered: [],
    filter: {
      integratorFilterModel: '',
      companyFilterModel: [],
      companyStatus: null
    },
    count: 0,
    // last table request (page + search); used to refresh the current page in place
    lastQuery: { from: 0, count: 100, value: '', field: '', fields: undefined, conditions: null },
    sortField: '',
    sortDesc: ''
  }),

  actions: {
    async postJsonRebroadcasts({
      companies = [],
      value,
      field
    }) {
      try {
        const requestData = {
          from: 0,
          count: 90000,
          value,
          field,
          companies
        };

        const response = await this.postAllRebroadcasts(requestData, true);

        return response;
        } catch (e) {
        throw new Error(e);
      }
    },
    async postAllRebroadcasts({ from = 0, count = 100, value, field, fields, sortField, sortDesc, conditions = null}, pagination) {
      try {
        if (!this.allRebroadcasts.length || pagination) {
          const formData = { from, count, value, field, fields, conditions };

          if (this.filter.integratorFilterModel) {
            formData.dealer = { id: this.filter.integratorFilterModel };
          }
          if (this.filter.companyFilterModel.length) {
            formData.companies = this.filter.companyFilterModel.map((comp) => ({ id: comp }));
          }
          if (this.sortDesc !== '') {
            formData.sortDesc = this.sortDesc;
            formData.sortField = this.sortField;
          }

          const response = await Rebroadcasts.postAllRebroadcasts(formData);

          if(count !== 90000) {
            this.lastQuery = { from, count, value, field, fields, conditions };
            this.allRebroadcasts = response.list;
            this.allRebroadcastsFiltered = response.list;
            this.allRebroadcastsMax = response.max;
          }

          return response;
        }
      } catch (e) {
        throw new Error(e);
      }
    },

    async deleteRebroadcasts(ids) {
      try {
        const response = await Rebroadcasts.deleteRebroadcasts(ids);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async updateRebroadcasts(data) {
      try {
        const response = await Rebroadcasts.updateRebroadcasts(data);
        // this.allRebroadcastsFiltered.map((val, key) => {
        //   if (response.id === val.id) {
        //     response.company_name = data.company_name;
        //     this.allRebroadcastsFiltered.splice(key, 1, response);
        //   }
        // });
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async createRebroadcast(data) {
      try {
        // the table reloads the current page afterwards, no local push (would overflow the page size)
        return await Rebroadcasts.createRebroadcast(data);
      } catch (e) {
        throw new Error(e);
      }
    },

    /**
     * Refreshes the page the user is looking at (same page size, same search).
     * If the page became empty (last row removed) steps back to the last non-empty one.
     * Returns the 1-based page number the table should show.
     */
    async reloadCurrentPage() {
      const query = { ...this.lastQuery };
      let response = await this.postAllRebroadcasts(query, true);

      if (!response?.list?.length && query.from > 0) {
        query.from = Math.max(0, Math.floor((response.max - 1) / query.count) * query.count);
        response = await this.postAllRebroadcasts(query, true);
      }

      return Math.floor(query.from / query.count) + 1;
    },

    async filterRebroadcasts(data) {
      try {
        const response = await this.postAllRebroadcasts({
          from: data.from ?? 0,
          count: data.count ?? this.lastQuery.count,
          value: data.value,
          field: data.valueFilter?.value || ''
        }, true);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async clearRebroadcastsFilter() {
      this.filter.integratorFilterModel = '';
      this.filter.companyFilterModel = [];
      const clearData = {
        companies: [],
        count: this.lastQuery.count,
        field: '',
        fields: [],
        from: 0,
        model: 'retranslators',
        value: '',
        conditions: []
      };
      try {
        await this.postAllRebroadcasts(clearData, true);
      } catch (error) {
        console.error('Error during clearing rebroadcasts:', error);
      }
    },

    async WEBSOCKET_CHANGE({ id, operation }) {
      if (operation === 'DELETE') return this.DELETE_REBROADCAST(id);

      const data = await Rebroadcasts.getRebroadcast(id);

      operation === 'POST' ? this.ADD_REBROADCAST(data) : this.UPDATE_REBROADCAST(data);
    },

    ADD_REBROADCAST(rebroadcast) {
      if (this.allRebroadcastsFiltered.some((x) => x.id === rebroadcast.id)) return;

      const { from, count } = this.lastQuery;
      const isLastPage = from + this.allRebroadcastsFiltered.length >= this.allRebroadcastsMax;

      this.allRebroadcastsMax += 1;

      // a new row belongs to the visible page only if it is the last one and there is room left
      if (isLastPage && this.allRebroadcastsFiltered.length < count) {
        this.allRebroadcasts = this.allRebroadcastsFiltered = [...this.allRebroadcastsFiltered, rebroadcast];
      }
    },

    UPDATE_REBROADCAST(rebroadcast) {
      const idx = this.allRebroadcastsFiltered.findIndex((elem) => elem.id === rebroadcast.id);

      if (idx === -1) return;

      this.allRebroadcasts = this.allRebroadcastsFiltered = [...this.allRebroadcastsFiltered.slice(0, idx), rebroadcast, ...this.allRebroadcastsFiltered.slice(idx + 1)];
    },

    DELETE_REBROADCAST(id) {
      if (this.allRebroadcastsFiltered.some((item) => item.id === id)) {
        this.allRebroadcastsMax = Math.max(0, this.allRebroadcastsMax - 1);
      }

      this.allRebroadcasts = this.allRebroadcastsFiltered = this.allRebroadcastsFiltered.filter((item) => item.id !== id);
    },
    updateSortedRebroadcasts(payload) {
      this.allRebroadcastsFiltered = payload.list;
    },
    CLEAR_REBROADCAST_FILTER() {
      console.log('CLEAR_REBROADCAST_FILTER');
      this.filter.integratorFilterModel = '';
      this.filter.companyFilterModel = [];
      this.filter.companyStatus = null;
    }
  },

  getters: {
    getRebroadcasts: (state) => state.allRebroadcastsFiltered
  }
});
